import React, { useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import PulseDashboard from './components/PulseDashboard';
import NotesPage from './components/NotesPage';
import SocialPage from './components/SocialPage';
import NarrativesPage from './components/NarrativesPage';
import AlertsPage from './components/AlertsPage';
import BacktestPage from './components/BacktestPage';
import Toaster from './components/Toaster';
import TokenDetailModal from './components/TokenDetailModal';
import NoteDetailModal from './components/NoteDetailModal';
import HashRouteHandler from './components/HashRouteHandler';
import CreateNoteModal from './components/CreateNoteModal';
import { TweetManagementModal } from './components/TweetManagement';
import { useWebSocket } from './hooks/useWebSocket';
import { useDataStore } from './stores/data/dataStore';
import { useToasterStore } from './stores/ui/toasterStore';
import { useUIStore } from './stores/ui/uiStore';
import { chromeNotification } from './utils/notification';
import SocialDetailModal from './components/SocialDetailModal';

// 페이지별 body/html 스타일 관리 컴포넌트
function PageStyleManager() {
  const location = useLocation();

  useEffect(() => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;

    // SocialPage나 BacktestPage일 때 overflow-y: auto, 나머지는 hidden
    if (location.pathname === '/social') {
      htmlElement.style.overflowY = 'auto';
      bodyElement.style.overflowY = 'auto';
    } else {
      htmlElement.style.overflowY = 'hidden';
      bodyElement.style.overflowY = 'hidden';
    }

    // cleanup: 컴포넌트 언마운트 시 기본값으로 복원 (선택사항)
    return () => {
      // 필요시 여기서 스타일 초기화
    };
  }, [location.pathname]);

  return null;
}

function App() {
  const { setConnectionStatus, initializeData, handleTokenUpdateMessage } = useDataStore();
  const { isVisible, message, type, duration, hideToaster } = useToasterStore();
  const { 
    isTokenDetailModalOpen, 
    closeTokenDetailModal, 
    isSocialDetailModalOpen, 
    closeSocialDetailModal,
    isNoteDetailModalOpen,
    closeNoteDetailModal,
    isTwitterSyncModalOpen,
    createNoteModal,
    closeTwitterSyncModal,
  } = useUIStore();

  // 콜백 함수들을 메모이제이션하여 불필요한 리렌더링 방지
  const handleOpen = useCallback(() => {
    setConnectionStatus({ isConnected: true, lastConnected: Date.now() });
  }, [setConnectionStatus]);

  const handleClose = useCallback(() => {
    setConnectionStatus({ isConnected: false });
  }, [setConnectionStatus]);

  const handleError = useCallback((error: Event) => {
    setConnectionStatus({ 
      isConnected: false, 
      error: error.toString() 
    });
  }, [setConnectionStatus]);

  const { 
    isConnected, 
    reconnectAttempts, 
    lastMessageTime, 
    healthCheckInterval 
  } = useWebSocket({
    url: `https://prod-sol-af-tracker.alpha-filter.xyz/aggregated-tokens`,
    onTokenUpdateMessage: handleTokenUpdateMessage,
    onOpen: handleOpen,
    onClose: handleClose,
    onError: handleError,
    healthCheckInterval: 30000, // 30초마다 헬스체크
  });

  useEffect(() => {
    initializeData();
  }, [initializeData]);

  // WebSocket 상태를 dataStore에 동기화
  useEffect(() => {
    setConnectionStatus({
      isConnected,
      reconnectAttempts,
      lastMessageTime,
      healthCheckInterval,
      isHealthCheckActive: true
    });
  }, [isConnected, reconnectAttempts, lastMessageTime, healthCheckInterval, setConnectionStatus]);


  // 알림 권한 요청
  useEffect(() => {
    const requestNotificationPermission = async () => {
      if (chromeNotification.isSupported()) {
        await chromeNotification.requestPermission();
      }
    };

    // 페이지 로드 후 1초 뒤에 권한 요청 (사용자 경험 개선)
    const timer = setTimeout(() => {
      requestNotificationPermission();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Router>
      <PageStyleManager />
      <div className={`App`}>
        <HashRouteHandler />
        <Routes>
          <Route path="/" element={<PulseDashboard isConnected={isConnected} reconnectAttempts={reconnectAttempts} />} />
          <Route path="/social" element={<SocialPage />} />
          <Route path="/notes" element={<NotesPage />} />
          <Route path="/narratives" element={<NarrativesPage />} />
          <Route path="/alerts" element={<AlertsPage />} />
          <Route path="/backtest" element={<BacktestPage />} />
        </Routes>
        <Toaster 
          isVisible={isVisible}
          message={message}
          type={type}
          duration={duration}
          onClose={hideToaster}
        />
        {
          isTokenDetailModalOpen && (
            <TokenDetailModal 
              isOpen={isTokenDetailModalOpen}
              onClose={closeTokenDetailModal}
            />
          )
        }
        {
          isSocialDetailModalOpen && (
            <SocialDetailModal
              isOpen={isSocialDetailModalOpen}
              onClose={closeSocialDetailModal}
            />
          )
        }
        {
          isNoteDetailModalOpen && (
            <NoteDetailModal
              isOpen={isNoteDetailModalOpen}
              onClose={closeNoteDetailModal}
            />
          )
        }
        <CreateNoteModal
          isOpen={isTwitterSyncModalOpen}
          onClose={closeTwitterSyncModal}
          tokenSymbol={createNoteModal?.tokenSymbol || ''}
          tokenAddress={createNoteModal?.tokenAddress || ''}
          tokenName={createNoteModal?.tokenName}
          tokenTwitter={createNoteModal?.tokenTwitter}
          tokenWebsite={createNoteModal?.tokenWebsite}
          tokenCategory={createNoteModal?.tokenCategory}
          syncId={createNoteModal?.syncId}
          existingAssociatedHandles={createNoteModal?.existingAssociatedHandles}
          existingCommunityId={createNoteModal?.existingCommunityId}
          onSuccess={createNoteModal?.onSuccess}
        />
        <TweetManagementModal />
      </div>
    </Router>
  );
}

export default App;
